import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { FormField, IAutocompleteProps } from 'core/components';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { useGetShippingMethods } from 'corporate/hooks';
import { corporateService } from 'corporate/services';
import { IShippingMethod } from 'corporate/types';

interface IProps extends IAutocompleteProps {
  defaultName?: string;
  initialValue?: IShippingMethod;
}

export const ShippingMethodFormField = ({ defaultName, initialValue, ...rest }: IProps) => {
  const { t } = useTranslation();

  const { control, errors, setValue, getValues } = useFormContext();

  const { records: shippingMethods } = useGetShippingMethods(
    {
      filters: { name: defaultName },
    },
    !!defaultName
  );

  // Checks the live value, not initialValue: a form whose defaults gain the method only after the
  // lookup, without a reset, has initialValue set while the field itself is still empty.
  useEffect(() => {
    if (!defaultName || getValues('shippingMethod')) {
      return;
    }

    const defaultShippingMethod = shippingMethods.find((method) => method.name === defaultName);

    if (defaultShippingMethod) {
      setValue('shippingMethod', defaultShippingMethod);
    }
  }, [defaultName, initialValue, getValues, setValue, shippingMethods]);

  return (
    <FormField
      name="shippingMethod"
      type={InputType.AUTOCOMPLETE}
      inputProps={{
        control,
        error: errors?.shippingMethod,
        label: t('labelShippingMethod'),
        getOptions: corporateService.getShippingMethods,
        getLabel: (option: IShippingMethod) =>
          `${option.name} ${option.carrier ? `(${option.carrier})` : ''}`,
        enabledOnFocus: false,
        ...rest,
      }}
    />
  );
};
